/**
 * AGROMX Frontend - Input Normalizer
 *
 * Normaliza el contenido de inputs en tiempo real para evitar errores comunes
 * cuando el usuario dicta por voz o pega texto de Excel/PDF. Casos:
 *   - Espacios entre caracteres (dictado por voz: "F T T M 5 ..." → "FTTM5...")
 *   - Minúsculas en campos que deben ser MAYÚSCULAS
 *   - Caracteres no permitidos (guiones en NS que deben ser alfanuméricos)
 *
 * Uso:
 *   import { normalizers, attachNormalizer } from '../utils/inputNormalizer.js';
 *   const input = document.getElementById('num_serie');
 *   attachNormalizer(input, normalizers.alfanumUpper);
 */

export const normalizers = {
  /**
   * Solo dígitos. Útil para: pedimento, teléfonos, códigos numéricos.
   * Elimina espacios y cualquier carácter no numérico.
   */
  numero: (v) => (v || '').replace(/\D/g, ''),

  /**
   * Alfanuméricos + guion + MAYÚSCULAS. Útil para: número de serie, serie de motor,
   * folios alfanuméricos. Elimina espacios y símbolos y convierte a uppercase.
   * El guion SE CONSERVA (B5c, 1-nov-2026): es la regla de las series del ERP
   * (validators/unidades.validator.js: letras, números y guiones) y antes
   * «PRUEBA-EJ-01» se guardaba como «PRUEBAEJ01», distinta de la del papel.
   */
  alfanumUpper: (v) => (v || '').replace(/[^A-Z0-9-]/gi, '').toUpperCase()
};

/**
 * Adjunta un listener `input` al elemento que normaliza el valor en cada cambio,
 * preservando la posición del cursor cuando se borra contenido inválido.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} el - Elemento de input.
 * @param {(value: string) => string} normalizer - Función que toma el valor crudo y devuelve el normalizado.
 */
export const attachNormalizer = (el, normalizer) => {
  if (!el || typeof normalizer !== 'function') return;
  el.addEventListener('input', (e) => {
    const start = e.target.selectionStart;
    const valorOriginal = e.target.value;
    const valorNormalizado = normalizer(valorOriginal);
    if (valorOriginal === valorNormalizado) return;
    e.target.value = valorNormalizado;
    // Reposicionar cursor: lo "movemos" hacia atrás tanto como caracteres se borraron
    // antes de la posición del cursor original.
    const diff = valorOriginal.length - valorNormalizado.length;
    const nuevaPos = Math.max(0, (start || 0) - diff);
    try { e.target.setSelectionRange(nuevaPos, nuevaPos); } catch (_) { /* algunos inputs no soportan setSelectionRange */ }
  });
};
